/**
 * Credit Card Utility Functions
 */

/**
 * Detect card brand from card number
 */
export const detectCardBrand = (cardNumber) => {
  const number = cardNumber.replace(/\s/g, '');

  const patterns = {
    visa: /^4/,
    mastercard: /^5[1-5]|^2[2-7]/,
    amex: /^3[47]/,
    discover: /^6(?:011|5)/,
    jcb: /^35(?:2[89]|[3-8])/,
    diners: /^3(?:0[0-5]|[68])/,
    unionpay: /^62/,
  };

  for (const [brand, pattern] of Object.entries(patterns)) {
    if (pattern.test(number)) {
      return brand;
    }
  }

  return 'unknown';
};

/** Card brands accepted for payment (Visa, Mastercard, JCB, American Express) */
export const SUPPORTED_CARD_BRANDS = ['visa', 'mastercard', 'jcb', 'amex'];

/**
 * Returns true if the card brand is accepted (Visa, Mastercard, JCB, Amex only).
 */
export const isCardBrandSupported = (brand) => {
  return brand && SUPPORTED_CARD_BRANDS.includes(brand);
};

/**
 * Format card number with spaces
 */
export const formatCardNumber = (value) => {
  const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
  const matches = v.match(/\d{4,16}/g);
  const match = (matches && matches[0]) || '';
  const parts = [];

  for (let i = 0, len = match.length; i < len; i += 4) {
    parts.push(match.substring(i, i + 4));
  }

  if (parts.length) {
    return parts.join(' ');
  } else {
    return value;
  }
};

/**
 * Format expiry date (MM/YY) – display month first, year second
 * Input: raw digits, Output: MM/YY format
 */
export const formatExpiry = (value) => {
  const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');

  if (v.length >= 2) {
    return v.substring(0, 2) + (v.length > 2 ? '/' + v.substring(2, 4) : '');
  }
  return v;
};

/**
 * Parse expiry from formatted string (MM/YY) to YYMM for API
 * Example: "01/39" (MM/YY) -> "3901" (YYMM)
 */
export const parseExpiryToYyMm = (formatted) => {
  const digits = formatted.replace(/\D/g, '');
  if (digits.length !== 4) return digits;
  const mm = digits.substring(0, 2);
  const yy = digits.substring(2, 4);
  return yy + mm; // YYMM
};

/**
 * @deprecated Use parseExpiryToYyMm for API. Kept for compatibility.
 */
export const parseExpiry = (formatted) => {
  return formatted.replace('/', '');
};

/**
 * Validate card number using Luhn algorithm
 */
export const validateCardNumber = (cardNumber) => {
  const number = cardNumber.replace(/\s/g, '');

  if (!/^\d{13,19}$/.test(number)) {
    return false;
  }

  let sum = 0;
  let isEven = false;

  for (let i = number.length - 1; i >= 0; i--) {
    let digit = parseInt(number[i], 10);

    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
};

/**
 * Validate expiry date (MM/YY format)
 * First 2 digits = month, last 2 digits = year
 */
export const validateExpiry = (expiry) => {
  const cleaned = expiry.replace(/\D/g, '');

  if (!/^\d{4}$/.test(cleaned)) {
    return false;
  }

  const month = parseInt(cleaned.substring(0, 2), 10);
  const year = parseInt(cleaned.substring(2, 4), 10);

  if (month < 1 || month > 12) {
    return false;
  }

  const now = new Date();
  const currentYear = now.getFullYear() % 100;
  const currentMonth = now.getMonth() + 1;

  if (year < currentYear || (year === currentYear && month < currentMonth)) {
    return false;
  }

  return true;
};

/**
 * Validate CVV
 */
export const validateCVV = (cvv, cardBrand) => {
  const length = cardBrand === 'amex' ? 4 : 3;
  const regex = new RegExp(`^\\d{${length}}$`);
  return regex.test(cvv);
};

/**
 * Get card brand icon URL
 */
export const getCardBrandIcon = (brand) => {
  const icons = {
    visa: '/assets/images/card-brands/visa.svg',
    mastercard: '/assets/images/card-brands/mastercard.svg',
    amex: '/assets/images/card-brands/amex.svg',
    discover: '/assets/images/card-brands/discover.svg',
    jcb: '/assets/images/card-brands/jcb.svg',
    diners: '/assets/images/card-brands/diners.svg',
    unionpay: '/assets/images/card-brands/unionpay.svg',
    unknown: '/assets/images/card-brands/generic-card.svg',
  };

  return icons[brand] || icons.unknown;
};

/**
 * Mask card number for display
 */
export const maskCardNumber = (cardNumber) => {
  const number = cardNumber.replace(/\s/g, '');
  const last4 = number.slice(-4);
  return `**** **** **** ${last4}`;
};
